
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Brain, Check, Settings, Shield, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';

interface CookieConsentMainProps {
  onAcceptAll: () => void;
  onRejectNonEssential: () => void;
  onCustomize: () => void;
  isMobile: boolean;
}

export const CookieConsentMain: React.FC<CookieConsentMainProps> = ({
  onAcceptAll,
  onRejectNonEssential,
  onCustomize,
  isMobile
}) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            <Shield className="w-6 h-6 text-purple-600" />
            <CardTitle id="cookie-consent-title" className="text-lg font-bold text-gray-900">
              We Value Your Privacy
            </CardTitle>
          </div>
          <Brain className="w-5 h-5 text-blue-500 animate-pulse" />
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p id="cookie-consent-description" className="text-sm text-gray-700 leading-relaxed">
          We use cookies and AI analytics (chat/voice) to enhance your experience. 
          Some data is processed to improve our services. You can customise preferences below.
        </p>

        <div className="flex flex-wrap items-center gap-x-2 text-sm">
          <Link
            to="/privacy"
            className="inline-flex min-h-11 items-center px-2 font-medium text-purple-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2"
          >
            Privacy Policy
          </Link>
          <span className="text-muted-foreground" aria-hidden="true">•</span>
          <button 
            type="button"
            onClick={() => setShowDetails(!showDetails)}
            className="inline-flex min-h-11 items-center px-2 font-medium text-purple-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-600 focus-visible:ring-offset-2"
            aria-expanded={showDetails}
            aria-controls="cookie-consent-details"
          >
            Cookie Details
          </button>
        </div>

        <Collapsible open={showDetails} onOpenChange={setShowDetails}>
          <CollapsibleContent id="cookie-consent-details" className="space-y-2">
            <div className="bg-gray-50 p-3 rounded-lg text-xs space-y-2">
              <div className="flex justify-between">
                <span className="font-medium">Essential</span>
                <span className="text-gray-500">Always Active</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Analytics</span>
                <span className="text-gray-500">30 days</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Marketing</span>
                <span className="text-gray-500">90 days</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">AI Data</span>
                <span className="text-gray-500">30 days</span>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        <div className={`space-y-2 ${isMobile ? 'pb-4' : ''}`}>
          <Button 
            type="button"
            onClick={onAcceptAll}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:opacity-90 min-h-[44px]"
          >
            <Check aria-hidden="true" />
            Accept All
          </Button>
          
          <Button 
            type="button"
            onClick={onCustomize}
            variant="outline"
            className="w-full min-h-[44px]"
          >
            <Settings className="w-4 h-4 mr-2" />
            Customise
          </Button>
          
          <Button 
            type="button"
            onClick={onRejectNonEssential}
            variant="ghost"
            className="w-full text-gray-600 min-h-[44px]"
          >
            <X aria-hidden="true" />
            Reject Non-Essential
          </Button>
        </div>
      </CardContent>
    </>
  );
};
